/**
 * @property {number} x
 * @property {number} y
 */
class Point {
	/**
	 * @param {number} x 
	 * @param {number} y 
	 */
	constructor(x, y) {
		this.x = x;
		this.y = y;
	}
}

/**
 * @property {Point} root
 * @property {Point} controlPoint1
 * @property {Point} controlPoint2
 */
class BezierPoint {
	/**
	 * @param {Point} root 
	 * @param {Point} controlPoint1 
	 * @param {Point} controlPoint2 
	 */
	constructor(root, controlPoint1, controlPoint2) {
		this.root = root;
		this.controlPoint1 = controlPoint1;
		this.controlPoint2 = controlPoint2;
	}
}

/**
 * @property {HTMLCanvasElement} canvas
 * @property {CanvasRenderingContext2D} context
 */
class CurveElement extends HTMLElement {
	/** @type {BezierPoint[]} */
	points = [];
	stroke = "#F4E28B";

	#updatePoints() {
		const groupRegex = /([\-0-9]+),\s*([\-0-9]+)\s+([\-0-9]+),\s*([\-0-9]+)\s+([\-0-9]+),\s*([\-0-9]+)/g;
		const points = this.getAttribute("points");

		if (!points) {
			return [];
		}

		this.points = Array.from(points.matchAll(groupRegex))
			.map(points => new BezierPoint(
				new Point(Number(points[1]), Number(points[2])),
				new Point(Number(points[3]), Number(points[4])),
				new Point(Number(points[5]), Number(points[6]))
			)) || [];
	}

	draw() {
		requestAnimationFrame(() => this.draw());

		const { width, height } = this.canvas;

		this.context.clearRect(0, 0, width, height);

		let pathBegin = false;

		for (let point of this.points) {
			if (!pathBegin) {
				this.context.beginPath();
				this.context.moveTo(point.root.x / 1000 * width, point.root.y / 1000 * height);
				pathBegin = true;
			} else {
				this.context.bezierCurveTo(
					point.controlPoint1.x / 1000 * width,
					point.controlPoint1.y / 1000 * height,
					point.controlPoint2.x / 1000 * width,
					point.controlPoint2.y / 1000 * height,
					point.root.x / 1000 * width,
					point.root.y / 1000 * height
				);
			}
		}

		this.context.strokeStyle = this.stroke;
		this.context.setLineDash([650, 48]);
		this.context.lineWidth = 8;
		this.context.stroke();

		if (this.hasAttribute("debug")) {
			for (let point of this.points) {
				this.context.beginPath();
				this.context.arc(point.root.x / 1000 * width, point.root.y / 1000 * height, 5, 0, 2 * Math.PI);
				this.context.fillStyle = "#f00";
				this.context.fill();

				this.context.beginPath();
				this.context.arc(point.controlPoint1.x / 1000 * width, point.controlPoint1.y / 1000 * height, 5, 0, 2 * Math.PI);
				this.context.fillStyle = "#0f0";
				this.context.fill();
					
				this.context.beginPath();
				this.context.arc(point.controlPoint2.x / 1000 * width, point.controlPoint2.y / 1000 * height, 5, 0, 2 * Math.PI);
				this.context.fillStyle = "#00f";
				this.context.fill();
			}
		}
	}

	connectedCallback() {
		const mutationObserver = new MutationObserver(mutations => {
			for (let mutation of mutations) {
				if (mutation.attributeName === "points") {
					this.#updatePoints();
				}

				if (mutation.attributeName === "stroke") {
					this.stroke = this.getAttribute("stroke");
				}
			}
		});

		const resizeObserver = new ResizeObserver(() => {
			this.canvas.width = this.clientWidth;
			this.canvas.height = this.clientHeight;
		});

		mutationObserver.observe(this, { attributes: true });
		resizeObserver.observe(this);

		this.canvas = document.createElement("canvas");
		this.context = this.canvas.getContext("2d");

		this.#updatePoints();

		this.stroke = this.getAttribute("stroke") || this.stroke;

		this.append(this.canvas);
		this.draw();
	}
}

customElements.define("arly-curve", CurveElement);